import { useEffect, useRef, useState } from 'react'
import React from 'react'
import Select, {
  components,
  ControlProps,
  createFilter,
  MultiValueProps,
  OptionProps,
} from 'react-select'

type Option = {
  value: string
  label: string
  avatar?: React.ReactNode
}

type MultiselectProps = {
  options: Option[]
  defaultSelected?: Option[]
  selected?: Option[]
  onChange: (selectedOptions: Option[] | null) => void
  onMenuOpen?: () => void
  loading?: boolean
  maxMenuHeight?: number
  placeholder?: string
  displayTags?: boolean
  customText?: string
  className?: string
  isSearchable?: boolean
  menuPlacement?: 'auto' | 'top' | 'bottom'
  maxTagDisplay?: number
}

const Control = (props: ControlProps<Option, true>) => {
  return (
    <components.Control
      {...props}
      className={`shadow appearance-none border rounded w-full text-gray-700 leading-tight focus:outline-none focus:ring-gray-200 ${props.className}`}
    />
  )
}

const Checkbox = (props: any) => {
  return (
    <div className='flex items-center cursor-pointer'>
      <input
        type='checkbox'
        className='h-4 w-4 rounded border-gray-300 text-gray-600 disabled:accent-gray-300 cursor-pointer focus:ring-gray-800 accent-primary-800'
        checked={props.isSelected}
        onChange={() => null}
      />
      <div className='ml-2'>{props.children}</div>
    </div>
  )
}

const OptionComponent = (props: OptionProps<Option, true>) => {
  return (
    <div>
      <components.Option {...props} className='bg-white'>
        <Checkbox {...props}>
          <div className='flex items-center gap-x-2'>
            {props.data.avatar && <div>{props.data.avatar}</div>}
            <div className='flex-auto truncate'>{props.label}</div>
          </div>
        </Checkbox>
      </components.Option>
    </div>
  )
}

const MultiValueLabel = (props: MultiValueProps<Option>) => {
  const { children, data } = props
  return (
    <div>
      <components.MultiValueLabel {...props}>
        <div className='flex items-center'>
          <div className='flex-auto truncate'>{data.label}</div>
        </div>
      </components.MultiValueLabel>
    </div>
  )
}

const ValueContainer = ({
  children,
  displayTags,
  customText,
  maxTagDisplay,
  ...props
}: any) => {
  const hasValue = props.hasValue
  const selectedValues = props.getValue() || []

  if (!displayTags && hasValue) {
    return (
      <components.ValueContainer {...props}>
        {customText}
      </components.ValueContainer>
    )
  }

  if (
    displayTags &&
    hasValue &&
    maxTagDisplay > 0 &&
    selectedValues.length > maxTagDisplay
  ) {
    // Convert children to array for manipulation
    const childArray = React.Children.toArray(children)

    // Separate tag components from input component
    // In react-select, the input is typically the last child
    const tagComponents = []
    let inputComponent = null

    for (let i = 0; i < childArray.length; i++) {
      const child = childArray[i] as React.ReactElement
      // Check if this is a multi-value tag component
      if (
        child &&
        typeof child === 'object' &&
        'props' in child &&
        child.props &&
        child.props.data &&
        child.props.data.value
      ) {
        // Only collect up to maxTagDisplay tags
        if (tagComponents.length < maxTagDisplay) {
          tagComponents.push(child)
        }
      } else {
        // This is likely the input component
        inputComponent = child
      }
    }

    return (
      <components.ValueContainer {...props}>
        {tagComponents}
        {inputComponent}
        <span className='ml-1'>
          +{selectedValues.length - maxTagDisplay} more
        </span>
      </components.ValueContainer>
    )
  }

  return (
    <components.ValueContainer {...props}>{children}</components.ValueContainer>
  )
}

const withCustomText =
  (
    WrappedComponent: React.ComponentType<any>,
    customText: string,
    displayTags: boolean,
    maxTagDisplay: number,
  ) =>
  (props: any) => {
    return (
      <WrappedComponent
        {...props}
        customText={customText}
        displayTags={displayTags}
        maxTagDisplay={maxTagDisplay}
      />
    )
  }

const ClearIndicator = () => {
  return null
}

const Multiselect: React.FC<MultiselectProps> = ({
  options,
  defaultSelected = [],
  selected = [],
  onChange,
  onMenuOpen,
  loading = false,
  maxMenuHeight = 200,
  placeholder = 'Select...',
  displayTags = true,
  customText = '',
  className = null,
  isSearchable = true,
  menuPlacement = 'bottom',
  maxTagDisplay = 0,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const selectRef = useRef<React.ElementRef<typeof Select>>(null)
  const [menuCloseTimeout, setMenuCloseTimeout] =
    useState<NodeJS.Timeout | null>(null)

  // Add state for menuIsOpen
  const [menuIsOpen, setMenuIsOpen] = useState(false)

  // Function to handle menu closing
  const handleMenuClose = () => {
    setMenuIsOpen(false)
  }

  const customStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    input: (provided: any) => ({
      ...provided,
      '& input[type="checkbox"]': {
        accentColor: 'black', // Controls the color of checkbox when checked (overrides browser default blue)
        // borderColor: '#000', // change the border color
        // color: '#000', // change the checkbox color
      },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'transparent' : undefined, // provided.backgroundColor,
      color: 'text-gray-900',
      ':hover': {
        backgroundColor: '#EAECF0', // change the hover border color
      },
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: '40px',
      // gray-300
      boxShadow: state.isFocused ? '0 0 0 1px #EAECF0' : 'none',
      borderColor: '#E5E7EB', // border-gray-200 color equivalent
      border: '1px solid #E5E7EB', // Add border with gray-200 color
      borderRadius: '0.5rem', // rounded-lg
      ':hover': {
        borderColor: '#667085', // change the hover border color
        boxShadow: '0 0 0 1px #667085', // change the hover boxShadow color
        // borderColor: 'transparent', // Keep it transparent on hover too
        // boxShadow: 'none', // Remove hover boxShadow
      },
    }),
  }

  const components = {
    ClearIndicator,
    Option: OptionComponent,
    MultiValueLabel: displayTags ? MultiValueLabel : null,
    ValueContainer: withCustomText(
      ValueContainer,
      customText,
      displayTags,
      maxTagDisplay,
    ),
    Control,
  }

  // Setup click event listener for closing the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuIsOpen &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        if (menuCloseTimeout) {
          clearTimeout(menuCloseTimeout)
        }

        setMenuIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuIsOpen, menuCloseTimeout])

  return (
    <div className={className} ref={wrapperRef}>
      <Select
        ref={selectRef}
        menuPortalTarget={document.body}
        isLoading={loading}
        isMulti={true}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={components}
        defaultValue={defaultSelected}
        value={selected}
        options={options}
        onChange={onChange}
        maxMenuHeight={maxMenuHeight}
        placeholder={placeholder}
        styles={customStyles}
        classNamePrefix='multiselect cursor-pointer text-gray-900 sm:text-sm sm:leading-6'
        filterOption={createFilter({ ignoreAccents: false })}
        isSearchable={isSearchable}
        menuIsOpen={menuIsOpen}
        menuPlacement={menuPlacement}
        onMenuOpen={() => {
          setTimeout(() => setMenuIsOpen(true), 200)

          if (onMenuOpen) {
            onMenuOpen()
          }
        }}
        onMenuClose={() => {
          if (menuCloseTimeout) {
            clearTimeout(menuCloseTimeout)
          }

          setMenuIsOpen(false)
        }}
        {...(maxTagDisplay > 0
          ? {
              controlShouldRenderValue: true,
              isClearable: true,
              hideSelectedOptions: false,
              isMulti: true,
              closeMenuOnSelect: false,
              backspaceRemovesValue: true,
            }
          : {})}
        {...(maxTagDisplay > 0 ? { maxValue: maxTagDisplay } : {})}
      />
    </div>
  )
}

export default Multiselect
